import React from "react";
import { Link } from "react-router-dom";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";
import { useConfig } from "../../../context/ConfigContext";
import { logEvent } from "../../../lib/analytics";

const WhySolarValley = () => {
  const config = useConfig();
  const {
    COMPANY_NAME,
    PLANTAS_SOLARES,
    MIN_PORCENT_FACT,
    GARANTY_YEARS,
    INICIO_DESDE,
  } = config;
  const features = [
    {
      id: 1,
      icon: "Award",
      title: "Experiencia Local",
      description: `Más de ${new Date()?.getFullYear() - INICIO_DESDE
        } años especializados en el clima y regulaciones guatemaltecas`,
      details: [
        `${PLANTAS_SOLARES || 100}+ instalaciones exitosas`,
        "Certificaciones locales",
        "Equipo técnico guatemalteco especializado",
      ],
      bgColor: "from-primary/10 to-primary/5",
      iconColor: "text-primary",
    },
    {
      id: 2,
      icon: "TrendingUp",
      title: "Ahorros Garantizados",
      description: `Garantizamos mínimo ${MIN_PORCENT_FACT || 80
        }% de reducción en tu factura eléctrica`,
      details: [
        "Análisis personalizado de consumo",
        `Garantía de rendimiento por ${GARANTY_YEARS} años`,
        "Monitoreo en tiempo real",
      ],
      bgColor: "from-success/10 to-success/5",
      iconColor: "text-success",
    },
    {
      id: 3,
      icon: "Shield",
      title: "Servicio Integral",
      description:
        "Desde el diseño hasta el mantenimiento, te acompañamos siempre",
      details: [
        "Consulta y diseño gratuito",
        "Instalación profesional",
        "Soporte técnico",
      ],
      bgColor: "from-secondary/10 to-secondary/5",
      iconColor: "text-secondary",
    },
  ];

  const guatemalanElements = [
    {
      icon: "Mountain",
      name: "Volcanes",
      description: "Instalaciones adaptadas a la geografía volcánica",
    },
    {
      icon: "Coffee",
      name: "Café",
      description: "Sistemas para fincas cafetaleras sostenibles",
    },
    {
      icon: "Bird",
      name: "Quetzal",
      description: "Compromiso con la biodiversidad guatemalteca",
    },
  ];

  return (
    <section className="py-16 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="Star" size={16} />
            <span>Por Qué Elegir {COMPANY_NAME}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-4">
            Tu Socio en Independencia Energética
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Combinamos tecnología de clase mundial con conocimiento local para
            ofrecerte la mejor experiencia en ahorro de energía con un sistema
            solar
          </p>
        </div>

        {/* Main Features Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {features?.map((feature, index) => (
            <div
              key={feature?.id}
              className={`relative p-8 rounded-2xl bg-gradient-to-br ${feature?.bgColor} solar-shadow-card solar-hover-lift group`}
            >
              {/* Feature Icon */}
              <div
                className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white solar-shadow-card mb-6 ${feature?.iconColor}`}
              >
                <Icon name={feature?.icon} size={32} />
              </div>

              {/* Feature Content */}
              <h3 className="text-xl font-semibold text-text-primary mb-3">
                {feature?.title}
              </h3>
              <p className="text-text-secondary mb-6 leading-relaxed">
                {feature?.description}
              </p>

              {/* Feature Details */}
              <ul className="space-y-2">
                {feature?.details?.map((detail, detailIndex) => (
                  <li
                    key={detailIndex}
                    className="flex items-center space-x-2 text-sm text-text-secondary"
                  >
                    <Icon
                      name="Check"
                      size={14}
                      className="text-success flex-shrink-0"
                    />
                    <span>{detail}</span>
                  </li>
                ))}
              </ul>

              {/* Hover Effect */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 solar-transition pointer-events-none"></div>
            </div>
          ))}
        </div>


        {/* CTA Section */}
        <div className="text-center bg-white rounded-2xl solar-shadow-elevated p-8 lg:p-12">
          <h3 className="text-2xl font-bold text-text-primary mb-4">
            ¿Listo para Comenzar tu Transición Solar?
          </h3>
          <p className="text-text-secondary mb-8 max-w-2xl mx-auto">
            Únete a las más de {PLANTAS_SOLARES} plantas solares que ya
            disfrutan de energía limpia y ahorros reales
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/calculadora">
              <Button
                variant="default"
                size="lg"
                iconName="Calculator"
                iconPosition="left"
                className="bg-cta-orange hover:bg-cta-orange/90"
                onClick={() => {
                  logEvent(
                    "Navegación",
                    "clic_boton",
                    "WhySolarValley: Calcular Mis Ahorros"
                  );
                }}
              >
                Calcular Mis Ahorros
              </Button>
            </Link>

            <Link to="/nosotros">
              <Button
                variant="outline"
                size="lg"
                iconName="Users"
                iconPosition="left"
                onClick={() => {
                  logEvent(
                    "Navegación",
                    "clic_boton",
                    "WhySolarValley: Conoce Nuestra Empresa"
                  );
                }}
              >
                Conoce Nuestra Empresa
              </Button>
            </Link>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center items-center gap-8 mt-8 pt-8 border-t border-border">
            <div className="flex items-center space-x-2 text-sm text-text-secondary">
              <Icon name="Shield" size={16} className="text-success" />
              <span>Garantía {GARANTY_YEARS} años</span>
            </div>
            <div className="flex items-center space-x-2 text-sm text-text-secondary">
              <Icon name="Award" size={16} className="text-primary" />
              <span>Instalación Certificada</span>
            </div>
            <div className="flex items-center space-x-2 text-sm text-text-secondary">
              <Icon name="Users" size={16} className="text-secondary" />
              <span>{PLANTAS_SOLARES}+ Clientes Satisfechos</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhySolarValley;
